/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { View } from '@components/atoms'
import { Seo, Footer } from '@components/systems'
import {
	mono,
	monoFont,
	monoForm,
	monoShared,
} from '@components/systems/monolith/monolith.tokens'
import { MONOLITH_NAV_HEIGHT } from '@components/systems/monolith/MonolithNav'
import { MonolithDeviceModal } from '@components/systems/monolith/MonolithDeviceModal'
import {
	deviceList,
	deviceStats,
	DeviceEntryType,
	DeviceManufacturerType,
} from '@src/configs/devices.generated'
import { DeviceStatsType } from '@components/systems/monolith/deviceDetail.types'
import { useDeviceIndex } from '@components/systems/monolith/useDeviceIndex'
import { WORKS_WITH_TOPIC, contactPath } from '@src/configs/contact.config'
import { getSeoEntry } from '@src/configs/seo.config'
import { buildBreadcrumbJsonLd, buildCollectionPageJsonLd } from '@utils'

const fmt = (n: number) => n.toLocaleString('en-US')

const LETTERS = ['#', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ']

/**
 * 딕셔너리 필터. 총계는 팩별 실측(두 팩에 다 있는 장비는 양쪽에 센다).
 * 두 팩은 성격이 다르다 — CC 는 번호 하나로 값을 보내고, SysEx 는 기종 전용 메시지를 보낸다.
 */
const FILTERS = [
	{ id: 'all', label: 'All' },
	{ id: 'cc', label: 'CC' },
	{ id: 'sysex', label: 'SysEx' },
] as const

type FilterIdType = typeof FILTERS[number]['id']

const totalOf = (stats: DeviceStatsType, id: FilterIdType) =>
	id === 'cc' ? stats.cc.devices : id === 'sysex' ? stats.sysex.devices : stats.devices

/** 이 페이지의 라우트. 앵커 href 를 여기서부터 적는다 — `#slug` 만 쓰면 `<base href="/">` 가 루트로 보낸다. */
const PAGE_PATH = '/works-with'

/** 이 딕셔너리로 장비를 부르는 제품 — 구조화 데이터의 `about` */
const CONTROLLERS = [
	{ name: 'Kedem 6', path: '/products/kdm6' },
	{ name: 'Kedem 4', path: '/products/kdm4' },
	{ name: 'Koinos PLS 8', path: '/products/pls8' },
]

/** 장비 추가·정정 요청 — Contact 의 Works with 토픽을 미리 골라 연다(Message 템플릿도 그쪽이 채운다) */
const REQUEST_PATH = contactPath(WORKS_WITH_TOPIC)

interface ActiveDeviceType {
	manufacturer: string
	device: DeviceEntryType
}

/**
 * Works with — 호환 장비 색인. `/works-with` 한 장으로 끝난다 (SITEMAP_PLAN §5 · §22).
 *
 * 데이터는 빌드 때 S3 딕셔너리에서 센 `devices.generated.ts` 하나다. 숫자·이름을 손으로 적지 않는다.
 * 제조사 하나 = 헤어라인 행 하나(4:8). 왼쪽 제조사명, 오른쪽 장비 칩. 장비별 하위 페이지는 없고
 * 칩의 id 가 앵커다(`#neural-dsp-quad-cortex`, DESIGN_SYSTEM §12.1). 칩을 누르면 그 장비의 CC · SysEx 표가
 * 전체 화면 모달(`MonolithDeviceModal`)로 열린다 — 상세는 `public/works-with/{slug}.json` 을 그때 받는다.
 *
 * **모달은 상태가 아니라 주소다.** 열려 있는 장비는 `location.hash` 하나로 정해지고, 여는 것은 라우터
 * push 다 — 그래서 뒤로가기가 모달만 닫고 이 페이지에 남으며, 앞으로가기가 같은 모달을 다시 연다.
 * 히스토리는 라우터 하나만 만진다(`window.history` 를 직접 부르면 라우터의 idx 와 어긋난다).
 * 검색은 클라이언트 필터 — 프리렌더는 전체 목록을 굽는다(GEO).
 */
const MonolithWorksWithPage = () => {
	const location = useLocation()
	const navigate = useNavigate()

	// 최신 색인이 오기 전까지는 번들 사본으로 그린다. 둘은 같은 모양이라 갈아 끼워도 화면이 안 튄다.
	const index = useDeviceIndex()
	const list: DeviceManufacturerType[] = index ? index.list : deviceList
	const stats: DeviceStatsType = index ? index.stats : deviceStats
	const filters = useMemo(
		() => FILTERS.map(aFilter => ({ ...aFilter, total: totalOf(stats, aFilter.id) })),
		[stats],
	)

	// 구조화 데이터 — 제조사 단위 목록. 앵커는 행 id 와 같다.
	const jsonLd = useMemo(
		() => [
			buildCollectionPageJsonLd({
				name: 'Works with',
				description: getSeoEntry(PAGE_PATH).description,
				path: PAGE_PATH,
				about: CONTROLLERS,
				items: list.map(aMaker => ({
					name: aMaker.name,
					path: `${PAGE_PATH}#${aMaker.slug}`,
				})),
			}),
			buildBreadcrumbJsonLd([
				{ name: 'Home', path: '/' },
				{ name: 'Works with', path: PAGE_PATH },
			]),
		],
		[list],
	)

	const [query, setQuery] = useState('')
	const [filter, setFilter] = useState<FilterIdType>('all')
	const needle = query.trim().toLowerCase()

	// 이 페이지가 직접 push 한 모달인지 — 그럴 때만 Close 가 뒤로가기다(딥링크로 들어왔으면 사이트를 떠난다).
	const pushedRef = useRef(false)

	// slug → 제조사 + 장비. 해시 딥링크와 칩 클릭이 같이 쓴다.
	// 장비 slug 는 늘 `제조사-장비`다(sync-devices.js) — 제조사 앵커(#strymon)와 한 해시 공간을
	// 쓰지만 형태가 달라 절대 겹치지 않고, 같은 이름의 장비가 새 제조사로 들어와도 주소가 안 바뀐다.
	const bySlug = useMemo(() => {
		const map = new Map<string, ActiveDeviceType>()
		list.forEach(aMaker => {
			aMaker.devices.forEach(aDevice => {
				map.set(aDevice.slug, { manufacturer: aMaker.name, device: aDevice })
			})
		})
		return map
	}, [list])

	// 열려 있는 장비 = 주소. 제조사 앵커(#strymon)는 여기 안 걸리므로 A→Z 점프는 모달을 열지 않는다.
	const active: ActiveDeviceType | null =
		bySlug.get(decodeURIComponent(location.hash.replace(/^#/, ''))) ?? null

	const openDevice = useCallback(
		(slug: string) => {
			if (!bySlug.has(slug)) return
			pushedRef.current = true
			navigate({ hash: `#${slug}` })
		},
		[bySlug, navigate],
	)

	const closeDevice = useCallback(() => {
		if (pushedRef.current) {
			pushedRef.current = false
			navigate(-1)
			return
		}
		navigate(
			{ pathname: location.pathname, search: location.search, hash: '' },
			{ replace: true },
		)
	}, [navigate, location.pathname, location.search])

	// 딕셔너리 필터를 먼저 걸고 그 위에 검색. 제조사명이 걸리면 그 제조사의 (필터를 통과한) 장비 전부.
	const rows = useMemo<DeviceManufacturerType[]>(() => {
		const matched: DeviceManufacturerType[] = []
		list.forEach(aMaker => {
			const pool =
				filter === 'all'
					? aMaker.devices
					: aMaker.devices.filter(aDevice =>
							filter === 'cc' ? aDevice.cc > 0 : aDevice.sysex > 0,
					  )
			if (pool.length === 0) return

			if (needle === '' || aMaker.name.toLowerCase().includes(needle)) {
				matched.push(pool === aMaker.devices ? aMaker : { ...aMaker, devices: pool })
				return
			}
			const devices = pool.filter(aDevice => aDevice.name.toLowerCase().includes(needle))
			if (devices.length > 0) matched.push({ ...aMaker, devices })
		})
		return matched
	}, [needle, filter, list])

	const shown = rows.reduce((sum, aMaker) => sum + aMaker.devices.length, 0)
	const total = (filters.find(aFilter => aFilter.id === filter) ?? filters[0]).total
	const isNarrowed = needle !== '' || filter !== 'all'

	// A→Z 점프 — 글자별 첫 제조사의 앵커. 숫자로 시작하는 제조사는 '#'.
	// 보이는 행에서 뽑는다 — 검색·필터로 사라진 제조사로 점프하지 않게, 그 글자는 저절로 비활성이 된다.
	const letters = useMemo(() => {
		const first = new Map<string, string>()
		rows.forEach(aMaker => {
			const ch = aMaker.name.charAt(0).toUpperCase()
			const key = /[A-Z]/.test(ch) ? ch : '#'
			if (!first.has(key)) first.set(key, aMaker.slug)
		})
		return LETTERS.map(key => ({ key, slug: first.get(key) }))
	}, [rows])

	// 목록 위·아래 두 곳에서 같은 Contact 로 간다. href 는 크롤러용, 이동은 라우터가 한다.
	const onRequest = (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault()
		navigate(REQUEST_PATH)
	}

	// 제조사 행으로 점프. 위치 계산은 브라우저에 맡기고(scroll-margin-top 이 네비 높이를 보정한다) 주소만 맞춘다.
	const onJump = (e: React.MouseEvent<HTMLAnchorElement>, slug: string) => {
		e.preventDefault()
		document.getElementById(slug)?.scrollIntoView({ block: 'start' })
		navigate(
			{ pathname: location.pathname, search: location.search, hash: `#${slug}` },
			{ replace: true },
		)
	}

	return (
		<View css={style}>
			<Seo jsonLd={jsonLd} />

			<View className="mnWrap">
				<View className="mnPageHeader">
					<h1 className="mnPageTitle">Works with</h1>
					<p className="mnPageLede">
						{fmt(stats.devices)} devices from {fmt(stats.manufacturers)} manufacturers.{' '}
						{fmt(stats.parameters)} MIDI parameters, on the unit.
					</p>
					{/*
						프리렌더되는 본문 한 문단. 딕셔너리 표는 칩을 눌러야 받아오므로 크롤러와 AI 에게는
						보이지 않는다 — 안에 무엇이 들어 있는지를 여기서 문장으로 적어둔다 (SEO_PLAN §12).
						JSON 경로도 같이 적는다. llms.txt 가 같은 경로를 가리킨다.
					*/}
					<p className="mnPageLede">
						Each entry lists the control-change numbers a device answers to, the value
						range for each, and what the values mean. System-exclusive messages follow
						where the maker publishes them. The same data is served as JSON: an index at{' '}
						<a className="mnPageLink" href="/works-with/index.json">
							/works-with/index.json
						</a>
						, and one file per device. Kedem and Koinos send these messages from their
						footswitches. The Koinos PLS 8 has ten.
					</p>
					<a className="mnCtaGhost mnRequest" href={REQUEST_PATH} onClick={onRequest}>
						Add or correct a device
					</a>
				</View>

				<View className="mnTools">
					<View className="mnControls">
						<View className="mnField mnSearch">
							<label className="mnLabel" htmlFor="mnDeviceSearch">
								Search
							</label>
							<input
								id="mnDeviceSearch"
								type="search"
								className="mnInput"
								placeholder="Manufacturer or device"
								value={query}
								onChange={e => setQuery(e.target.value)}
								autoComplete="off"
								spellCheck={false}
							/>
						</View>

						<View className="mnField">
							<span className="mnLabel" id="mnDictLabel">
								Dictionary
							</span>
							<View className="mnFilters" role="group" aria-labelledby="mnDictLabel">
								{filters.map(aFilter => (
									<button
										key={aFilter.id}
										type="button"
										className={`mnFilter${filter === aFilter.id ? ' on' : ''}`}
										aria-pressed={filter === aFilter.id}
										onClick={() => setFilter(aFilter.id)}
									>
										<span className="mnFilterLabel">{aFilter.label}</span>
										<span className="mnFilterCount">{fmt(aFilter.total)}</span>
									</button>
								))}
							</View>
							{isNarrowed && (
								<span className="mnMeta mnCount">
									{fmt(shown)} of {fmt(total)} devices
								</span>
							)}
						</View>
					</View>

					<nav className="mnLetters" aria-label="Jump to manufacturer">
						{letters.map(aLetter =>
							aLetter.slug !== undefined ? (
								<a
									key={aLetter.key}
									className="mnLetter"
									href={`${PAGE_PATH}#${aLetter.slug}`}
									onClick={e => onJump(e, aLetter.slug as string)}
								>
									{aLetter.key}
								</a>
							) : (
								<span key={aLetter.key} className="mnLetter off">
									{aLetter.key}
								</span>
							),
						)}
					</nav>
				</View>

				<View className="mnList">
					{rows.map(aMaker => (
						<View key={aMaker.slug} id={aMaker.slug} className="mnRow">
							<View className="mnRowHead">
								<h2 className="mnRowTitle">{aMaker.name}</h2>
								<span className="mnMeta">
									{aMaker.devices.length}{' '}
									{aMaker.devices.length === 1 ? 'device' : 'devices'}
								</span>
							</View>

							<View className="mnRowBody">
								<ul className="mnChips">
									{aMaker.devices.map(aDevice => (
										<li key={aDevice.slug}>
											<button
												type="button"
												id={aDevice.slug}
												className="mnChip"
												aria-haspopup="dialog"
												title={`${aMaker.name} ${aDevice.name} — ${aDevice.cc} CC, ${aDevice.sysex} SysEx`}
												onClick={() => openDevice(aDevice.slug)}
											>
												<span className="mnChipName">{aDevice.name}</span>
												{aDevice.cc > 0 && (
													<span className="mnChipCount">
														<span className="mnChipKind">CC</span>
														{fmt(aDevice.cc)}
													</span>
												)}
												{aDevice.sysex > 0 && (
													<span className="mnChipCount">
														<span className="mnChipKind">SysEx</span>
														{fmt(aDevice.sysex)}
													</span>
												)}
											</button>
										</li>
									))}
								</ul>
							</View>
						</View>
					))}

					{rows.length === 0 && (
						<View className="mnEmptyRow">
							<span className="mnMeta">No match</span>
						</View>
					)}
				</View>

				<View className="mnNote">
					<p>
						What goes out is standard MIDI. A device absent from this list still takes
						Program Change and Control Change. A name on this list means Judahs Studio
						knows its parameters.
					</p>
					<a className="mnCtaGhost mnRequest" href={REQUEST_PATH} onClick={onRequest}>
						Add or correct a device
					</a>
				</View>
			</View>

			<Footer />

			{active !== null && (
				<MonolithDeviceModal
					manufacturer={active.manufacturer}
					device={active.device}
					onClose={closeDevice}
				/>
			)}
		</View>
	)
}

export default MonolithWorksWithPage

const style = css({
	...monoShared,
	...monoForm,
	position: 'relative',
	width: '100%',

	// 페이지 헤더 — 1열. 제목은 전폭, 소제목은 그 아래(DESIGN_SYSTEM §6.2). 4:8 은 두 단어 제목(Works with)이 줄바꿈돼 폐기.
	'.mnPageHeader': {
		padding: 'clamp(64px, 8.3vw, 120px) 0 clamp(48px, 6.25vw, 90px)',

		'.mnPageTitle': {
			fontSize: 'clamp(56px, 7.6vw, 110px)',
			lineHeight: 1,
			fontWeight: 100,
			letterSpacing: '-0.01em',
			color: mono.ink,
		},
		'.mnPageLede': {
			fontSize: 19,
			lineHeight: 1.6,
			fontWeight: 300,
			color: mono.dim,
			maxWidth: 520,
			marginTop: 'clamp(20px, 2vw, 28px)',
			fontVariantNumeric: 'tabular-nums',
		},
		'.mnPageLink': {
			color: mono.ink,
			textDecoration: 'underline',
			textUnderlineOffset: 3,
			textDecorationThickness: 1,
		},
		'.mnRequest': {
			marginTop: 'clamp(28px, 3vw, 40px)',
		},
	},

	// 검색 + A→Z 점프. 아래 행들과 같은 4:8 이라 입력이 제조사 열, 글자가 장비 열 위에 선다.
	'.mnTools': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 4fr) minmax(0, 8fr)',
		gap: 48,
		alignItems: 'end',
		paddingBottom: 40,

		// 좌열 = 걸러내는 것(검색 + 딕셔너리), 우열 = 이동(A→Z). 둘을 섞지 않는다.
		'.mnControls': {
			maxWidth: 420,
			display: 'flex',
			flexDirection: 'column',
			gap: 28,

			'.mnField': {
				gap: 12,
			},
			'.mnSearch': {
				gap: 8,
			},
			'.mnCount': {
				marginTop: 4,
				fontVariantNumeric: 'tabular-nums',
			},
		},

		// 딕셔너리 필터 — §5 칩 라디오. 선택 = 보더·글자 ink + 500.
		'.mnFilters': {
			display: 'flex',
			flexWrap: 'wrap',
			gap: 8,

			'.mnFilter': {
				display: 'inline-flex',
				alignItems: 'baseline',
				gap: 8,
				padding: '10px 18px',
				border: `1px solid ${mono.lineSoft}`,
				borderRadius: 999,
				background: 'transparent',
				fontFamily: monoFont.sans,
				fontSize: 13,
				lineHeight: 1.2,
				cursor: 'pointer',
				transition: 'border-color 200ms ease-out, color 200ms ease-out',

				'.mnFilterLabel': {
					fontWeight: 400,
					color: mono.dim,
				},
				'.mnFilterCount': {
					fontWeight: 400,
					fontSize: 12,
					fontVariantNumeric: 'tabular-nums',
					color: mono.faint,
				},

				'&:hover .mnFilterLabel': {
					color: mono.ink,
				},
				'&.on': {
					borderColor: mono.ink,
					'.mnFilterLabel': {
						fontWeight: 500,
						color: mono.ink,
					},
				},
				'&:focus-visible': {
					outline: `1px solid ${mono.ink}`,
					outlineOffset: 3,
				},
				'@media (prefers-reduced-motion: reduce)': {
					transition: 'none',
				},
			},
		},

		'.mnLetters': {
			display: 'flex',
			flexWrap: 'wrap',
			gap: 4,
			paddingBottom: 4,

			'.mnLetter': {
				display: 'inline-flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: 30,
				height: 30,
				fontSize: 13,
				fontWeight: 500,
				color: mono.dim,
				borderBottom: '1px solid transparent',
				transition: 'color 200ms ease-out, border-color 200ms ease-out',

				'&:hover': {
					color: mono.ink,
					borderBottomColor: mono.ink,
				},
				'&.off': {
					color: mono.lineSoft,
					pointerEvents: 'none',
				},
				'@media (prefers-reduced-motion: reduce)': {
					transition: 'none',
				},
			},
		},

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
			gap: 28,
		},
	},

	'.mnList': {
		borderTop: `1px solid ${mono.line}`,
	},

	// 제조사 행 — 4:8. 왼쪽 이름 + 장비 수, 오른쪽 장비 칩.
	'.mnRow': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 4fr) minmax(0, 8fr)',
		gap: 48,
		padding: 'clamp(36px, 3.6vw, 52px) 0',
		borderBottom: `1px solid ${mono.line}`,
		scrollMarginTop: MONOLITH_NAV_HEIGHT + 24,

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
			gap: 24,
		},
	},

	'.mnRowHead': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: 14,

		'.mnRowTitle': {
			fontSize: 'clamp(28px, 2.8vw, 40px)',
			lineHeight: 1.1,
			fontWeight: 300,
			color: mono.ink,
			overflowWrap: 'anywhere',
		},
		'.mnMeta': {
			fontVariantNumeric: 'tabular-nums',
		},
	},

	'.mnRowBody': {
		minWidth: 0,
	},

	// 장비 칩 — DESIGN_SYSTEM §5 칩 규격. 버튼: 누르면 장비 모달. `:target` 이 선택 상태(앵커 착지).
	'.mnChips': {
		listStyle: 'none',
		margin: 0,
		padding: 0,
		display: 'flex',
		flexWrap: 'wrap',
		gap: 10,

		li: {
			display: 'contents',
		},

		'.mnChip': {
			display: 'inline-flex',
			alignItems: 'baseline',
			gap: 10,
			padding: '10px 18px',
			border: `1px solid ${mono.lineSoft}`,
			borderRadius: 999,
			backgroundColor: 'transparent',
			fontFamily: monoFont.sans,
			fontSize: 13,
			lineHeight: 1.2,
			textAlign: 'left',
			cursor: 'pointer',
			scrollMarginTop: MONOLITH_NAV_HEIGHT + 24,
			transition: 'border-color 200ms ease-out, color 200ms ease-out',

			'.mnChipName': {
				fontWeight: 500,
				color: mono.dim,
				overflowWrap: 'anywhere',
			},
			// 어느 딕셔너리의 수인지 붙인다 — 팩이 둘 다 있으면 둘 다 선다(9 대).
			'.mnChipCount': {
				display: 'inline-flex',
				alignItems: 'baseline',
				gap: 4,
				fontWeight: 400,
				fontVariantNumeric: 'tabular-nums',
				color: mono.faint,
				whiteSpace: 'nowrap',

				'.mnChipKind': {
					fontSize: 11,
					fontWeight: 500,
					letterSpacing: '.08em',
					color: mono.faint,
				},
			},

			'&:hover, &:target': {
				borderColor: mono.ink,
				'.mnChipName': {
					color: mono.ink,
				},
			},
			'&:focus-visible': {
				outline: `1px solid ${mono.ink}`,
				outlineOffset: 3,
			},
			'@media (prefers-reduced-motion: reduce)': {
				transition: 'none',
			},
		},
	},

	'.mnEmptyRow': {
		padding: 'clamp(36px, 3.6vw, 52px) 0',
		borderBottom: `1px solid ${mono.line}`,
	},

	// 목록 뒤 사실 한 줄 — 목록이 전부가 아니라는 것.
	'.mnNote': {
		padding: 'clamp(48px, 5vw, 72px) 0 clamp(64px, 8.3vw, 120px)',

		p: {
			fontSize: 16,
			lineHeight: 1.7,
			fontWeight: 300,
			color: mono.dim,
			maxWidth: 620,
		},
		'.mnRequest': {
			marginTop: 32,
		},
	},
})
